import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import {
    AbsenceDataServiceName,
    AbsenceEndpoints,
    IAbsenceDataService,
} from '../../../DataServices/AbsenceDataService';
import { Lazy } from '../../../Helpers/Lazy';
import {
    Absence,
    AbsenceDetailsApiDataResponse,
    AbsenceListOrder,
    AbsenceListResponse,
} from '../../../Models/Absence/Absence';
import { BaseApiDataResponse } from '../../../Models/ApiData';
import { ExportFileType, ExportUtils } from '../../../Utils/ExportUtils';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { AuthServiceName, IAuthService } from '../Authentication/AuthenticationService';
import { DownloadServiceName, IDownloadService } from '../Download/DownloadService';
import { I18nServiceName, II18nService } from '../I18n/I18nService';

export interface IAbsenceService {
    listAbsence: (
        cts: XHRRequestCanceler,
        params: {
            search?: string;
            order?: AbsenceListOrder;
        }
    ) => Promise<AbsenceListResponse>;

    getAbsence: (
        cts: XHRRequestCanceler,
        absenceId: string
    ) => Promise<AbsenceDetailsApiDataResponse>;

    updateAbsence: (
        absenceId: string,
        absence: Absence,
        cts: XHRRequestCanceler
    ) => Promise<AbsenceDetailsApiDataResponse>;

    createAbsence: (
        absence: Absence,
        cts: XHRRequestCanceler
    ) => Promise<AbsenceDetailsApiDataResponse>;

    exportAbsenceList: (
        cts: XHRRequestCanceler,
        search: string,
        order?: AbsenceListOrder
    ) => Promise<void>;

    deleteAbsence: (absenceId: string, cts: XHRRequestCanceler) => Promise<BaseApiDataResponse>;
}

class AbsenceService extends BaseReduxService implements IAbsenceService {
    private readonly absenceDataService: Lazy<IAbsenceDataService>;
    private readonly i18nService: Lazy<II18nService>;
    private readonly downloadService: Lazy<IDownloadService>;
    private readonly authService: Lazy<IAuthService>;

    constructor() {
        super();
        this.absenceDataService = IoC.getLazy<IAbsenceDataService>(AbsenceDataServiceName);
        this.i18nService = IoC.getLazy<II18nService>(I18nServiceName);
        this.downloadService = IoC.getLazy<IDownloadService>(DownloadServiceName);
        this.authService = IoC.getLazy<IAuthService>(AuthServiceName);
    }

    deleteAbsence = (absenceId: string, cts: XHRRequestCanceler): Promise<BaseApiDataResponse> => {
        try {
            return this.absenceDataService.value().deleteAbsence(absenceId, cts);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    listAbsence = (
        cts: XHRRequestCanceler,
        params: { search?: string | undefined; order?: AbsenceListOrder | undefined }
    ): Promise<AbsenceListResponse> => {
        try {
            return this.absenceDataService.value().listAbsence(
                {
                    search: params.search,
                    order: params.order,
                },
                cts
            );
        } catch (err) {
            return Promise.reject(err);
        }
    };

    getAbsence = (
        cts: XHRRequestCanceler,
        absenceId: string
    ): Promise<AbsenceDetailsApiDataResponse> => {
        try {
            return this.absenceDataService.value().getAbsence(cts, absenceId);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    updateAbsence = async (
        absenceId: string,
        absence: Absence,
        cts: XHRRequestCanceler
    ): Promise<AbsenceDetailsApiDataResponse> => {
        try {
            const response = await this.absenceDataService
                .value()
                .updateAbsence(absenceId, absence, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    createAbsence = async (
        absence: Absence,
        cts: XHRRequestCanceler
    ): Promise<AbsenceDetailsApiDataResponse> => {
        try {
            const response = await this.absenceDataService.value().createAbsence(absence, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            this.toastApiError(this.i18nService.value().getString(`Error_${err.error_code}` || ''));
            return Promise.reject(err);
        }
    };

    exportAbsenceList = async (
        cts: XHRRequestCanceler,
        search: string,
        order?: AbsenceListOrder | undefined
    ): Promise<void> => {
        try {
            const params: { [key: string]: string } = {};
            params['search'] = search;
            if (order) {
                params['order'] = order.valueOf();
            }
            const url: string = ExportUtils.getExportUrlWithParams(
                {
                    url: AbsenceEndpoints.EXPORT_ABSENCE_LIST,
                    auth: await this.authService.value().getAuthorization(),
                },
                params
            );
            this.downloadService.value().downloadFile(url, 'Absences', ExportFileType.EXCEL, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };
}

const AbsenceServiceName = 'AbsenceService';
export { AbsenceService, AbsenceServiceName };
